/* eslint-disable */
import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { Search, ZoomIn, ZoomOut, Maximize2, Download, Eye, ChevronRight, ChevronDown } from 'react-feather';

// ─── VCD Parser ───────────────────────────────────────────────────────────────
function parseVCD(vcdContent) {
    if (!vcdContent || typeof vcdContent !== 'string') return null;

    const signals = {};   // symbol -> { name, width, values: [{time, val}], symbol, scope }
    const scopes = [];   // scope stack
    let currentTime = 0;
    let maxTime = 0;
    let inDefs = true;

    for (let raw of vcdContent.split('\n')) {
        const line = raw.trim();
        if (!line) continue;

        if (inDefs) {
            if (line.startsWith('$scope')) {
                const parts = line.split(/\s+/);
                if (parts[2]) scopes.push(parts[2]);
                continue;
            }
            if (line.startsWith('$upscope')) {
                scopes.pop();
                continue;
            }
            if (line.startsWith('$var')) {
                const p = line.split(/\s+/).filter(Boolean);
                // $var type width symbol name [$end]
                const width = parseInt(p[2], 10) || 1;
                const symbol = p[3];
                const name = p[4];
                if (symbol && name) {
                    signals[symbol] = {
                        name,
                        fullName: scopes.length > 0 ? `${scopes.join('.')}.${name}` : name,
                        width,
                        values: [],
                        symbol,
                        scope: scopes.join('.')
                    };
                }
                continue;
            }
            if (line.startsWith('$enddefinitions')) {
                inDefs = false;
                continue;
            }
            continue;
        }

        if (line.startsWith('#')) {
            currentTime = parseInt(line.slice(1), 10) || 0;
            if (currentTime > maxTime) maxTime = currentTime;
        } else if (line.startsWith('b') || line.startsWith('B')) {
            const p = line.split(/\s+/).filter(Boolean);
            if (p.length >= 2) {
                const val = p[0].slice(1);
                const sym = p[1];
                if (signals[sym]) signals[sym].values.push({ time: currentTime, val });
            }
        } else if (line.length >= 2) {
            const val = line[0];
            const sym = line.slice(1).trim();
            if (signals[sym]) signals[sym].values.push({ time: currentTime, val });
        }
    }

    // Determine time unit suffix (default ns)
    let unit = 'ns';

    return { signals, maxTime, unit };
}

// Get signal value at a given time
function getValueAt(values, time) {
    if (!values || values.length === 0) return '?';
    let v = values[0].val;
    for (const e of values) {
        if (e.time > time) break;
        v = e.val;
    }
    return v;
}

// ─── Radix Formatting Helpers ────────────────────────────────────────────────
function binToHex(bin) {
    if (!bin || bin === '?' || /[xzXZ]/.test(bin)) return bin || '?';
    try {
        const clean = bin.replace(/[^01]/g, '');
        if (!clean) return bin;
        const n = (typeof window !== 'undefined' && window.BigInt) ? window.BigInt(`0b${clean}`) : parseInt(clean, 2);
        return n.toString(16).toUpperCase();
    } catch {
        return bin;
    }
}

function binToUnsignedDec(bin) {
    if (!bin || bin === '?' || /[xzXZ]/.test(bin)) return bin || '?';
    try {
        const clean = bin.replace(/[^01]/g, '');
        if (!clean) return bin;
        return (typeof window !== 'undefined' && window.BigInt) ? window.BigInt(`0b${clean}`).toString(10) : parseInt(clean, 2).toString(10);
    } catch {
        return bin;
    }
}

function binToSignedDec(bin) {
    if (!bin || bin === '?' || /[xzXZ]/.test(bin)) return bin || '?';
    try {
        const clean = bin.replace(/[^01]/g, '');
        if (!clean) return bin;
        const len = clean.length;
        let n = (typeof window !== 'undefined' && window.BigInt) ? window.BigInt(`0b${clean}`) : parseInt(clean, 2);
        // If highest bit is 1, convert two's complement
        if (clean[0] === '1' && len > 1) {
            if (typeof window !== 'undefined' && window.BigInt) {
                n = n - (window.BigInt(1) << window.BigInt(len));
            } else {
                n = n - Math.pow(2, len);
            }
        }
        return n.toString(10);
    } catch {
        return bin;
    }
}

function formatSignalValue(val, radix = 'hex', width = 1) {
    if (val === null || val === undefined || val === '') return '—';
    if (width === 1) return val;
    if (/[xzXZ]/.test(val)) return val;

    switch (radix) {
        case 'bin':
            return `${width}'b${val}`;
        case 'dec':
            return binToUnsignedDec(val);
        case 'signed':
            return binToSignedDec(val);
        case 'hex':
        default:
            return `0x${binToHex(val)}`;
    }
}

// ─── Color palette ────────────────────────────────────────────────────────────
const SIGNAL_COLORS = [
    '#38bdf8', '#34d399', '#f59e0b', '#c084fc', '#fb7185',
    '#22d3ee', '#a3e635', '#f97316', '#e879f9', '#2dd4bf'
];

// ─── Dimensions ───────────────────────────────────────────────────────────────
const ROW_H = 42;
const LABEL_W = 220;
const HEADER_H = 44;
const PAD_RIGHT = 60;

// ─── Main Component ───────────────────────────────────────────────────────────
const WaveformViewer = ({ vcdContent, theme, viewerType = 'EPWave', formatType = 'VCD' }) => {
    // Zoom & Scroll
    const [zoom, setZoom] = useState(1);
    const [scrollLeft, setScrollLeft] = useState(0);

    // Dual Cursors (T1: Cyan, T2: Amber)
    const [cursorA, setCursorA] = useState(null);
    const [cursorB, setCursorB] = useState(null);
    const [activeCursor, setActiveCursor] = useState('A'); // 'A' | 'B'

    // Radix mode: 'hex' | 'bin' | 'dec' | 'signed'
    const [globalRadix, setGlobalRadix] = useState('hex');
    const [customRadix, setCustomRadix] = useState({}); // symbol -> radix

    // Search / Filter
    const [filterQuery, setFilterQuery] = useState('');

    // Bus expansion state (symbol -> boolean)
    const [expandedBuses, setExpandedBuses] = useState({});

    const waveRef = useRef(null);
    const containerRef = useRef(null);

    const data = useMemo(() => {
        if (!vcdContent) return null;
        return parseVCD(vcdContent);
    }, [vcdContent]);

    const { signals, maxTime, unit } = data || { signals: {}, maxTime: 0, unit: 'ns' };
    const allSignals = useMemo(() => Object.values(signals), [signals]);

    // Filtered signal list
    const visibleSignals = useMemo(() => {
        if (!filterQuery.trim()) return allSignals;
        const q = filterQuery.toLowerCase();
        return allSignals.filter(s => s.name.toLowerCase().includes(q) || s.fullName.toLowerCase().includes(q));
    }, [allSignals, filterQuery]);

    const timeScale = 60 * zoom; // px per time unit
    const effectiveMaxTime = Math.max(maxTime, 10);
    const totalW = Math.max(effectiveMaxTime * timeScale + PAD_RIGHT, 300);

    // Calculate total height accounting for expanded bus bits
    const totalRowsCount = useMemo(() => {
        let count = 0;
        for (const sig of visibleSignals) {
            count += 1;
            if (sig.width > 1 && expandedBuses[sig.symbol]) {
                count += sig.width;
            }
        }
        return count;
    }, [visibleSignals, expandedBuses]);

    const totalH = totalRowsCount * ROW_H;

    // ── Cursors & Delta Calculation ──────────────────────────────────────────
    const delta = useMemo(() => {
        if (cursorA === null || cursorB === null) return null;
        const dt = Math.abs(cursorB - cursorA);
        if (dt === 0) return { dt: 0, freqStr: 'N/A' };
        // Assume unit is ns: 1 / (dt * 1e-9) = (1000 / dt) MHz
        const freqMhz = (1000 / dt).toFixed(2);
        return { dt, freqStr: `${freqMhz} MHz` };
    }, [cursorA, cursorB]);

    // Handle click on the waveform grid to set Cursor A or B
    const handleWaveClick = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const relX = e.clientX - rect.left;
        const t = Math.round(relX / timeScale);
        const clamped = Math.max(0, Math.min(t, effectiveMaxTime));

        if (e.shiftKey || activeCursor === 'B') {
            setCursorB(clamped);
            setActiveCursor('A');
        } else {
            setCursorA(clamped);
            if (cursorB === null) {
                // Default T2 to some distance if not yet set
                setCursorB(Math.min(clamped + 10, effectiveMaxTime));
            }
        }
    }, [timeScale, effectiveMaxTime, activeCursor, cursorB]);

    // Scroll sync
    const handleScroll = useCallback((e) => {
        setScrollLeft(e.currentTarget.scrollLeft);
    }, []);

    // Zoom controls
    const zoomIn = useCallback(() => setZoom(z => Math.min(z * 1.4, 50)), []);
    const zoomOut = useCallback(() => setZoom(z => Math.max(z / 1.4, 0.05)), []);
    const fitAll = useCallback(() => {
        if (!containerRef.current || effectiveMaxTime === 0) return;
        const w = containerRef.current.clientWidth - LABEL_W - PAD_RIGHT;
        setZoom(Math.max(0.05, w / effectiveMaxTime / 60));
    }, [effectiveMaxTime]);

    // Zoom to selection (T1 to T2)
    const zoomToSelection = useCallback(() => {
        if (cursorA === null || cursorB === null || cursorA === cursorB) return;
        const dt = Math.abs(cursorB - cursorA);
        if (dt === 0 || !containerRef.current) return;
        const w = containerRef.current.clientWidth - LABEL_W - PAD_RIGHT;
        setZoom(Math.max(0.05, w / dt / 60));
    }, [cursorA, cursorB]);

    // Toggle bus expansion
    const toggleBus = (symbol) => {
        setExpandedBuses(prev => ({ ...prev, [symbol]: !prev[symbol] }));
    };

    // Cycle signal radix
    const cycleRadix = (symbol) => {
        const radices = ['hex', 'dec', 'bin', 'signed'];
        const current = customRadix[symbol] || globalRadix;
        const nextIdx = (radices.indexOf(current) + 1) % radices.length;
        setCustomRadix(prev => ({ ...prev, [symbol]: radices[nextIdx] }));
    };

    // Export raw VCD
    const downloadVcd = () => {
        if (!vcdContent) return;
        const blob = new Blob([vcdContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'dump.vcd';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // Time ticks for ruler
    const tickInterval = useMemo(() => {
        if (!effectiveMaxTime || effectiveMaxTime <= 0) return 10;
        const raw = effectiveMaxTime / Math.max(totalW / 90, 2);
        if (raw <= 0 || !isFinite(raw)) return 10;
        const log = Math.log10(raw);
        if (!isFinite(log)) return 10;
        const mag = Math.pow(10, Math.floor(log));
        for (const m of [1, 2, 5, 10, 20, 50, 100]) {
            if (raw <= m * mag) return Math.max(m * mag, 1);
        }
        return Math.max(mag * 100, 1);
    }, [effectiveMaxTime, totalW]);

    const ticks = useMemo(() => {
        const res = [];
        const interval = tickInterval > 0 ? tickInterval : 10;
        for (let t = 0; t <= effectiveMaxTime + interval; t += interval) {
            res.push(t);
            if (res.length > 800) break;
        }
        return res;
    }, [effectiveMaxTime, tickInterval]);

    const isLight = theme === 'light';
    const bg = isLight ? '#f8fafc' : '#080d1a';
    const labelBg = isLight ? '#f1f5f9' : '#0c1222';
    const border = isLight ? '#cbd5e1' : '#1e293b';
    const textCol = isLight ? '#0f172a' : '#f1f5f9';
    const mutedCol = isLight ? '#64748b' : '#64748b';
    const headerBg = isLight ? '#e2e8f0' : '#0a0f1d';

    // ── No data state ────────────────────────────────────────────────────────
    if (!data || allSignals.length === 0) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                height: '100%', gap: '12px',
                color: mutedCol, background: bg
            }}>
                <span style={{ fontSize: '32px' }}>📈</span>
                <strong style={{ fontSize: '13px', color: textCol }}>No Waveform Data Generated</strong>
                <p style={{ fontSize: '11px', margin: 0, maxWidth: '340px', textAlign: 'center', lineHeight: 1.6 }}>
                    Select <strong>EPWave viewer (VCD format)</strong> in the left sidebar and run your simulation. The VCD waveform will stream and render here automatically.
                </p>
            </div>
        );
    }

    return (
        <div style={{
            display: 'flex', flexDirection: 'column', height: '100%',
            background: bg, color: textCol, fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            fontSize: '11px', userSelect: 'none', overflow: 'hidden'
        }}>
            {/* ── Top Engineering Toolbar ───────────────────────────────────── */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 14px', borderBottom: `1px solid ${border}`,
                background: headerBg, flexShrink: 0, gap: '12px', flexWrap: 'wrap'
            }}>
                {/* Left: Viewer Title & Radix selector */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ color: '#38bdf8', fontWeight: 'bold', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>📈</span>
                        <span>{viewerType}</span>
                        <span style={{ fontSize: '10px', color: mutedCol }}>({formatType})</span>
                    </span>

                    <div style={{ width: 1, height: 16, background: border, margin: '0 4px' }} />

                    {/* Global Radix Switcher */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                        <span style={{ color: mutedCol, fontSize: '10px' }}>Radix:</span>
                        <select
                            value={globalRadix}
                            onChange={(e) => setGlobalRadix(e.target.value)}
                            style={{
                                background: isLight ? '#ffffff' : '#1e293b',
                                color: textCol,
                                border: `1px solid ${border}`,
                                borderRadius: '4px',
                                padding: '2px 6px',
                                fontSize: '11px',
                                outline: 'none',
                                cursor: 'pointer'
                            }}
                        >
                            <option value="hex">Hex (0x)</option>
                            <option value="bin">Binary (b)</option>
                            <option value="dec">Unsigned Dec</option>
                            <option value="signed">Signed Dec</option>
                        </select>
                    </div>
                </div>

                {/* Center: Dual Cursor Measurements (Delta & Frequency) */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '3px 10px', borderRadius: '4px',
                    background: isLight ? '#ffffff' : 'rgba(15, 23, 42, 0.6)',
                    border: `1px solid ${border}`, fontSize: '11px'
                }}>
                    <span style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                        T1: {cursorA !== null ? `${cursorA} ${unit}` : '—'}
                    </span>
                    <span style={{ color: mutedCol }}>|</span>
                    <span style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                        T2: {cursorB !== null ? `${cursorB} ${unit}` : '—'}
                    </span>
                    {delta && (
                        <>
                            <span style={{ color: mutedCol }}>|</span>
                            <span style={{ color: '#10b981', fontWeight: 'bold' }}>
                                Δt: {delta.dt} {unit}
                            </span>
                            <span style={{ color: mutedCol }}>|</span>
                            <span style={{ color: '#a855f7', fontWeight: 'bold' }}>
                                f: {delta.freqStr}
                            </span>
                        </>
                    )}
                </div>

                {/* Right: Zoom controls, Selection Fit, Download VCD */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                        onClick={zoomOut}
                        title="Zoom Out"
                        style={{
                            padding: '3px 8px', background: isLight ? '#ffffff' : '#1e293b',
                            border: `1px solid ${border}`, color: textCol,
                            borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center'
                        }}
                    >
                        <ZoomOut size={12} />
                    </button>

                    <span style={{ color: mutedCol, fontSize: '10px', minWidth: '36px', textAlign: 'center' }}>
                        {(zoom * 100).toFixed(0)}%
                    </span>

                    <button
                        onClick={zoomIn}
                        title="Zoom In"
                        style={{
                            padding: '3px 8px', background: isLight ? '#ffffff' : '#1e293b',
                            border: `1px solid ${border}`, color: textCol,
                            borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center'
                        }}
                    >
                        <ZoomIn size={12} />
                    </button>

                    <button
                        onClick={fitAll}
                        title="Fit All"
                        style={{
                            padding: '3px 8px', background: isLight ? '#ffffff' : '#1e293b',
                            border: `1px solid ${border}`, color: textCol,
                            borderRadius: '4px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                    >
                        <Maximize2 size={11} /> Fit
                    </button>

                    <button
                        onClick={zoomToSelection}
                        title="Zoom to T1-T2 Range"
                        disabled={!delta || delta.dt === 0}
                        style={{
                            padding: '3px 8px', background: isLight ? '#ffffff' : '#1e293b',
                            border: `1px solid ${border}`, color: delta && delta.dt > 0 ? textCol : mutedCol,
                            borderRadius: '4px', cursor: delta && delta.dt > 0 ? 'pointer' : 'not-allowed', fontSize: '11px'
                        }}
                    >
                        Δ Range
                    </button>

                    <div style={{ width: 1, height: 16, background: border, margin: '0 4px' }} />

                    <button
                        onClick={downloadVcd}
                        title="Download Raw VCD File"
                        style={{
                            padding: '3px 8px', background: '#3b82f6',
                            border: 'none', color: '#ffffff',
                            borderRadius: '4px', cursor: 'pointer', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                    >
                        <Download size={11} /> VCD
                    </button>
                </div>
            </div>

            {/* ── Body: Fixed Label Column + Waveform Grid ───────────────────── */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }} ref={containerRef}>
                {/* Fixed Label Column */}
                <div style={{
                    width: LABEL_W, flexShrink: 0, display: 'flex',
                    flexDirection: 'column', borderRight: `1px solid ${border}`,
                    background: labelBg, zIndex: 2
                }}>
                    {/* Signal Search / Filter Header */}
                    <div style={{
                        height: HEADER_H, borderBottom: `1px solid ${border}`,
                        display: 'flex', alignItems: 'center', padding: '0 8px',
                        background: headerBg, gap: '6px'
                    }}>
                        <Search size={12} color={mutedCol} />
                        <input
                            type="text"
                            placeholder="Filter signals..."
                            value={filterQuery}
                            onChange={(e) => setFilterQuery(e.target.value)}
                            style={{
                                width: '100%', background: 'transparent',
                                border: 'none', outline: 'none',
                                color: textCol, fontSize: '11px'
                            }}
                        />
                    </div>

                    {/* Signal Names & Value Readouts */}
                    <div style={{ flex: 1, overflowY: 'hidden' }}>
                        {visibleSignals.map((sig, sIdx) => {
                            const color = SIGNAL_COLORS[sIdx % SIGNAL_COLORS.length];
                            const isVec = sig.width > 1;
                            const isExpanded = isVec && expandedBuses[sig.symbol];
                            const currentRadix = customRadix[sig.symbol] || globalRadix;

                            const valA = cursorA !== null ? getValueAt(sig.values, cursorA) : null;
                            const displayA = formatSignalValue(valA, currentRadix, sig.width);

                            return (
                                <React.Fragment key={sig.symbol}>
                                    {/* Main Signal Row */}
                                    <div style={{
                                        height: ROW_H, borderBottom: `1px solid ${border}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '0 8px', gap: '6px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', flex: 1 }}>
                                            {isVec ? (
                                                <button
                                                    onClick={() => toggleBus(sig.symbol)}
                                                    title={isExpanded ? 'Collapse Bus Bits' : 'Expand Bus Bits'}
                                                    style={{
                                                        background: 'transparent', border: 'none',
                                                        color: mutedCol, cursor: 'pointer', padding: 0,
                                                        display: 'flex', alignItems: 'center'
                                                    }}
                                                >
                                                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                </button>
                                            ) : (
                                                <span style={{ width: 12 }} />
                                            )}

                                            <span
                                                title={sig.fullName}
                                                style={{
                                                    color, fontWeight: 'bold', fontSize: '11px',
                                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {sig.name}
                                                {isVec && <span style={{ color: mutedCol, fontSize: '9px', marginLeft: 2 }}>[{sig.width - 1}:0]</span>}
                                            </span>
                                        </div>

                                        {/* Value Readout & Radix Toggle */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{
                                                fontSize: '10px', color: '#94a3b8',
                                                fontFamily: 'monospace', maxWidth: '64px',
                                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                            }}>
                                                {displayA}
                                            </span>

                                            {isVec && (
                                                <button
                                                    onClick={() => cycleRadix(sig.symbol)}
                                                    title={`Current Radix: ${currentRadix.toUpperCase()}. Click to change.`}
                                                    style={{
                                                        fontSize: '8px', padding: '1px 3px',
                                                        borderRadius: '2px', border: `1px solid ${border}`,
                                                        background: 'transparent', color: mutedCol,
                                                        cursor: 'pointer', textTransform: 'uppercase'
                                                    }}
                                                >
                                                    {currentRadix[0]}
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Expanded Bus Bit Rows */}
                                    {isExpanded && Array.from({ length: sig.width }).map((_, bitIdx) => {
                                        const bitNum = sig.width - 1 - bitIdx;
                                        const bitValA = valA && !/[xzXZ]/.test(valA) ? valA[bitIdx] : '?';

                                        return (
                                            <div
                                                key={`${sig.symbol}-bit-${bitNum}`}
                                                style={{
                                                    height: ROW_H, borderBottom: `1px solid ${border}`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    padding: '0 8px 0 24px', background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'
                                                }}
                                            >
                                                <span style={{ color: mutedCol, fontSize: '10px' }}>
                                                    {sig.name}[{bitNum}]
                                                </span>
                                                <span style={{ fontSize: '10px', color: '#94a3b8', fontFamily: 'monospace' }}>
                                                    {bitValA}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* Scrollable Waveform Grid */}
                <div
                    style={{ flex: 1, overflow: 'auto', position: 'relative' }}
                    onScroll={handleScroll}
                    ref={waveRef}
                >
                    <svg
                        width={totalW}
                        height={HEADER_H + totalH + 4}
                        onClick={handleWaveClick}
                        style={{ display: 'block', cursor: 'crosshair' }}
                    >
                        {/* Background grid lines */}
                        {ticks.map(t => (
                            <line
                                key={t}
                                x1={t * timeScale} x2={t * timeScale}
                                y1={HEADER_H} y2={HEADER_H + totalH}
                                stroke={border} strokeWidth="0.5"
                            />
                        ))}

                        {/* ── Time Ruler ─────────────────────────────────────── */}
                        <rect x={0} y={0} width={totalW} height={HEADER_H} fill={headerBg} />
                        {ticks.map(t => (
                            <g key={t} transform={`translate(${t * timeScale}, 0)`}>
                                <line y1={HEADER_H - 8} y2={HEADER_H} stroke={border} strokeWidth="1" />
                                <text
                                    x={0} y={HEADER_H - 12}
                                    fontSize="9" textAnchor="middle"
                                    fill={mutedCol}
                                >
                                    {t}
                                </text>
                            </g>
                        ))}
                        <text x={6} y={HEADER_H - 2} fontSize="9" fill={mutedCol} fontWeight="bold">
                            Time ({unit})
                        </text>

                        {/* ── Signal Waveforms ───────────────────────────────── */}
                        {(() => {
                            let currentRowY = HEADER_H;
                            return visibleSignals.map((sig, sIdx) => {
                                const color = SIGNAL_COLORS[sIdx % SIGNAL_COLORS.length];
                                const isVec = sig.width > 1;
                                const isExpanded = isVec && expandedBuses[sig.symbol];
                                const currentRadix = customRadix[sig.symbol] || globalRadix;

                                const yBase = currentRowY;
                                const yHigh = yBase + 6;
                                const yLow = yBase + ROW_H - 8;
                                const yMid = yBase + ROW_H / 2;

                                currentRowY += ROW_H;

                                // Expanded bit rows advance row tracker
                                const bitRows = isExpanded ? Array.from({ length: sig.width }) : [];
                                if (isExpanded) {
                                    currentRowY += sig.width * ROW_H;
                                }

                                return (
                                    <g key={sig.symbol}>
                                        {/* Row separator */}
                                        <line
                                            x1={0} x2={totalW}
                                            y1={yBase + ROW_H} y2={yBase + ROW_H}
                                            stroke={border} strokeWidth="0.5"
                                        />

                                        {/* Main Waveform Trace */}
                                        {sig.values.map((v, i) => {
                                            const t1 = v.time;
                                            const t2 = sig.values[i + 1]?.time ?? effectiveMaxTime;
                                            const x1 = t1 * timeScale;
                                            const x2 = t2 * timeScale;
                                            const segW = x2 - x1;
                                            const isX = /[xX]/.test(v.val);
                                            const isZ = /[zZ]/.test(v.val);
                                            const isUnk = isX || isZ;
                                            const segColor = isUnk ? '#f59e0b' : color;

                                            if (!isVec) {
                                                // Scalar digital waveform
                                                const isHigh = v.val === '1';
                                                const yThis = isHigh ? yHigh : yLow;
                                                const yNext = sig.values[i + 1]?.val === '1' ? yHigh : yLow;
                                                return (
                                                    <React.Fragment key={i}>
                                                        {isUnk ? (
                                                            <rect
                                                                x={x1} y={yHigh} width={segW}
                                                                height={yLow - yHigh}
                                                                fill="rgba(245,158,11,0.15)"
                                                                stroke="#f59e0b" strokeWidth="0.5"
                                                            />
                                                        ) : (
                                                            <line
                                                                x1={x1} x2={x2}
                                                                y1={yThis} y2={yThis}
                                                                stroke={segColor} strokeWidth="2"
                                                            />
                                                        )}
                                                        {/* Digital transition edge */}
                                                        {i < sig.values.length - 1 && !isUnk && (
                                                            <line
                                                                x1={x2} x2={x2}
                                                                y1={yThis} y2={yNext}
                                                                stroke={segColor} strokeWidth="1.5"
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                );
                                            } else {
                                                // Multi-bit bus polygon
                                                const formattedVal = formatSignalValue(v.val, currentRadix, sig.width);
                                                const trap = Math.min(6, segW / 4);
                                                const path = [
                                                    `M ${x1} ${yMid}`,
                                                    `L ${x1 + trap} ${yHigh}`,
                                                    `L ${x2 - trap} ${yHigh}`,
                                                    `L ${x2} ${yMid}`,
                                                    `L ${x2 - trap} ${yLow}`,
                                                    `L ${x1 + trap} ${yLow}`,
                                                    'Z'
                                                ].join(' ');
                                                return (
                                                    <g key={i}>
                                                        <path
                                                            d={path}
                                                            fill={isUnk ? 'rgba(245,158,11,0.12)' : `${segColor}18`}
                                                            stroke={segColor} strokeWidth="1.5"
                                                        />
                                                        {segW > 28 && (
                                                            <text
                                                                x={x1 + segW / 2} y={yMid + 3.5}
                                                                fontSize="9" textAnchor="middle"
                                                                fill={segColor} fontWeight="bold"
                                                            >
                                                                {formattedVal}
                                                            </text>
                                                        )}
                                                    </g>
                                                );
                                            }
                                        })}

                                        {/* Expanded Bit Traces */}
                                        {isExpanded && bitRows.map((_, bIdx) => {
                                            const bYBase = yBase + (bIdx + 1) * ROW_H;
                                            const bYHigh = bYBase + 6;
                                            const bYLow = bYBase + ROW_H - 8;

                                            return (
                                                <g key={`expanded-bit-group-${bIdx}`}>
                                                    <line
                                                        x1={0} x2={totalW}
                                                        y1={bYBase + ROW_H} y2={bYBase + ROW_H}
                                                        stroke={border} strokeWidth="0.5"
                                                    />
                                                    {sig.values.map((v, i) => {
                                                        const t1 = v.time;
                                                        const t2 = sig.values[i + 1]?.time ?? effectiveMaxTime;
                                                        const x1 = t1 * timeScale;
                                                        const x2 = t2 * timeScale;
                                                        const bVal = !/[xzXZ]/.test(v.val) ? v.val[bIdx] : 'x';
                                                        const isHigh = bVal === '1';
                                                        const isUnk = bVal === 'x' || bVal === 'z';
                                                        const yThis = isHigh ? bYHigh : bYLow;

                                                        const nextRaw = sig.values[i + 1]?.val;
                                                        const nextBVal = nextRaw && !/[xzXZ]/.test(nextRaw) ? nextRaw[bIdx] : 'x';
                                                        const yNext = nextBVal === '1' ? bYHigh : bYLow;

                                                        return (
                                                            <React.Fragment key={i}>
                                                                {isUnk ? (
                                                                    <rect
                                                                        x={x1} y={bYHigh} width={x2 - x1}
                                                                        height={bYLow - bYHigh}
                                                                        fill="rgba(245,158,11,0.12)"
                                                                        stroke="#f59e0b" strokeWidth="0.5"
                                                                    />
                                                                ) : (
                                                                    <line
                                                                        x1={x1} x2={x2}
                                                                        y1={yThis} y2={yThis}
                                                                        stroke="#64748b" strokeWidth="1.5"
                                                                    />
                                                                )}
                                                                {i < sig.values.length - 1 && !isUnk && (
                                                                    <line
                                                                        x1={x2} x2={x2}
                                                                        y1={yThis} y2={yNext}
                                                                        stroke="#64748b" strokeWidth="1.5"
                                                                    />
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </g>
                                            );
                                        })}
                                    </g>
                                );
                            });
                        })()}

                        {/* ── Cursor A (Primary: Cyan) ─────────────────────────── */}
                        {cursorA !== null && (
                            <g transform={`translate(${cursorA * timeScale}, 0)`}>
                                <line
                                    y1={0} y2={HEADER_H + totalH}
                                    stroke="#06b6d4" strokeWidth="1.5"
                                    strokeDasharray="4 2"
                                />
                                <polygon points="0,0 -5,6 5,6" fill="#06b6d4" />
                                <rect x={-26} y={7} width={52} height={14} rx={3} fill="#06b6d4" />
                                <text x={0} y={18} fontSize="8" textAnchor="middle" fill="#0f172a" fontWeight="bold">
                                    T1:{cursorA}{unit}
                                </text>
                            </g>
                        )}

                        {/* ── Cursor B (Secondary: Amber) ───────────────────────── */}
                        {cursorB !== null && (
                            <g transform={`translate(${cursorB * timeScale}, 0)`}>
                                <line
                                    y1={0} y2={HEADER_H + totalH}
                                    stroke="#f59e0b" strokeWidth="1.5"
                                    strokeDasharray="4 2"
                                />
                                <polygon points="0,0 -5,6 5,6" fill="#f59e0b" />
                                <rect x={-26} y={7} width={52} height={14} rx={3} fill="#f59e0b" />
                                <text x={0} y={18} fontSize="8" textAnchor="middle" fill="#0f172a" fontWeight="bold">
                                    T2:{cursorB}{unit}
                                </text>
                            </g>
                        )}

                        {/* ── Delta Measurement Region Highlighting ──────────── */}
                        {cursorA !== null && cursorB !== null && (
                            <rect
                                x={Math.min(cursorA, cursorB) * timeScale}
                                y={HEADER_H}
                                width={Math.abs(cursorB - cursorA) * timeScale}
                                height={totalH}
                                fill="rgba(16, 185, 129, 0.05)"
                                pointerEvents="none"
                            />
                        )}
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default WaveformViewer;
